import { useCallback } from 'react';
import type { Message, MessageV2 } from '@/hooks/useCommunication';
import { useCommunication } from '@/hooks/useCommunication';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { collectLines, readReader, useSerialPort } from '@/hooks/useSerialPort';
import { BwsNb2LoraEncoder } from '@/lib/protocol/encoder/nb-2-lora';
import { BWSNB2ReadParser } from '@/lib/protocol/parser/nb-2';
import type { BwsNb2Lora } from '@/lib/protocol/parser/nb-2-lora';
import { BwsNb2LoraParser } from '@/lib/protocol/parser/nb-2-lora';
import { BWSNB2WriteParser } from '@/lib/protocol/parser/write/nb-2';
import { isIccid, isImei, sleep, typedObjectEntries } from '@/lib/utils';
import { createValidationLog, validateConfiguration } from '@/lib/utils/configuration-validation';
import { lorawanTimestamp, verifyLorawanKeys } from '@/lib/utils/generate-key-lora-wan';
import type {
  BwsLoraConfig,
  IConfigurationProfile,
} from '@/types/production/configuration-profile';
import type { Device } from '@/types/production/device';

namespace Namespace {
  export interface Equipment {
    firmware: string;
    serial: string;
    imei?: string | undefined;
    iccid?: string | undefined;
    lora_keys?: Partial<Device['Equipment']['lora_keys']>;
  }
  export interface Detected {
    port: ISerialPort;
    equipment: Equipment;
  }

  export type Profile = IConfigurationProfile<BwsLoraConfig>['config'];

  export type ConfigKeys = string;
}

const readResponse = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  command: string,
  timeout: number = 500, // Optimized: matches legacy-next-js (stable)
  check?: string,
): Promise<string | undefined> => {
  const decoder = new TextDecoder();
  let buffer = '';

  const base = command.replace('\r\n', '');
  const cmp = check ? check : base.includes('=') ? base : `${base}=`;

  const timeoutPromise = new Promise<undefined>((resolve) =>
    setTimeout(() => resolve(undefined), timeout),
  );

  const readPromise = (async () => {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value);

      console.info('[RAW DATA]', buffer);

      const lines = buffer.split('\r\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        // agora compara usando cmp
        if (line.length > 0 && line.includes(cmp)) {
          return line;
        }
      }
    }
    return undefined;
  })();

  return Promise.race([readPromise, timeoutPromise]);
};

const generateMessages = (profile: IConfigurationProfile): Record<Namespace.ConfigKeys, string> => {
  const response = {} as Record<Namespace.ConfigKeys, string>;
  Object.entries({
    ...profile.config.general,
    ...profile.config.specific,
  }).forEach(([message, args]) => {
    const _message = BwsNb2LoraEncoder.encoder({
      command: message,
      args,
    } as any);
    if (!_message) return;
    response[message as Namespace.ConfigKeys] = _message;
  });
  return response;
};

export const useNB2Lora = () => {
  const { ports, openPort, closePort, getReader, writeToPort, requestPort } = useSerialPort({});

  // hook that handles communication process, like retries, delay between messages
  const { sendMultipleMessages, send } = useCommunication<ISerialPort>({
    openTransport: async (transport) => {
      // settleDelay 150ms (default 350): agiliza cada ciclo de comunicação
      await openPort(
        transport,
        {
          baudRate: 115200,
          stopBits: 2,
          bufferSize: 1024,
        },
        150,
      );
    },
    closeTransport: closePort,
    sendMessage: async (port, msg: Message<string, { check?: string; delay_before?: number }>) => {
      const reader = await getReader(port);
      if (!reader) throw new Error('Reader não disponível');
      const { command, timeout, delay_before, check } = msg;
      if (delay_before) await sleep(delay_before);
      console.info('-------------------------');
      console.info('[MESSAGE SENT]', command);
      await writeToPort(port, command);
      const response = await readResponse(reader, command, timeout, check);
      console.info('[RESPONSE MATCHED]', response);

      await reader.cancel();
      reader.releaseLock();
      return response;
    },
    sendMessageV2: async (
      port,
      msg: MessageV2<{
        delay_before?: number;
        delay_after?: number;
        check?: string;
      }>,
    ) => {
      const { command, delay_before, delay_after } = msg;
      if (delay_before) await sleep(delay_before);
      await writeToPort(port, command);
      console.log('[COMMAND]', command);
      if (delay_after) await sleep(delay_after);
    },
    options: {
      delayBetweenMessages: 300,
      maxRetriesPerMessage: 3,
      maxOverallRetries: 2,
    },
  });

  // functions to interact with E34G via serial port
  const handleDetection = useCallback(
    async (ports: ISerialPort[]) => {
      // Detecção enxuta (igual ao NB2 puro): apenas reconhecimento NB2. O LRKEYS NÃO
      // é enviado aqui — ele despejava ~76 linhas (incluindo lixo/parciais) no buffer,
      // e como a detecção roda em loop a cada 5s, esse resíduo contaminava as leituras
      // da configuração (RIP1/RIAP vinham corrompidos). As chaves LoRa passam a ser
      // lidas só na identificação.
      const messages = [
        { command: 'RIMEI\r\n' },
        { command: 'ICCID\r\n' },
        { command: 'RINS\r\n' },
        { command: 'RFW\r\n' },
      ] as const;
      return await Promise.all(
        ports.map(async (port) => {
          try {
            const { pos } = await send({
              transport: port,
              messages,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                const result = await readReader(reader, 10000);
                return result;
              },
            });

            const lines = pos ?? [];
            const pick = <T>(fn: (line: string) => T | undefined): T | undefined =>
              lines.map((line) => fn(line)).find((value) => value !== undefined);

            return {
              port,
              response: {
                imei: pick(BwsNb2LoraParser.imei),
                iccid: pick(BwsNb2LoraParser.iccid),
                serial: pick(BwsNb2LoraParser.serial),
                firmware: pick(BwsNb2LoraParser.firmware),
              },
            };
          } catch (error) {
            if (
              (error as Error).name === 'NetworkError' ||
              (error as Error).message?.includes('Failed to open serial port')
            ) {
              return { port };
            }
            console.error('[ERROR] handleDetection use-nb-2-lora', error);
            return { port };
          }
        }),
      );
    },
    [send, getReader],
  );
  const handleGetConfig = useCallback(
    async (detected: Namespace.Detected[]) => {
      const messages = [
        { command: 'RODM\r\n', key: 'odometer', delay_before: 1000 },
        {
          command: 'RCW\r\n',
          key: 'data_transmission_sleep',
        },
        {
          command: 'RIG1\r\n',
          key: 'virtual_ignition_12v',
        },
        {
          command: 'RIG2\r\n',
          key: 'virtual_ignition_24v',
        },
        { command: 'RFH\r\n', key: 'heading' },
        {
          command: 'RFHV\r\n',
          key: 'heading_event_mode',
        },
        {
          command: 'RFA\r\n',
          key: 'heading_detection_angle',
        },
        {
          command: 'RFV1\r\n',
          key: 'speed_alert_threshold',
        },
        {
          command: 'RFTON\r\n',
          key: 'accel_threshold_for_ignition_on',
        },
        {
          command: 'RFTOF\r\n',
          key: 'accel_threshold_for_ignition_off',
        },
        {
          command: 'RFAV\r\n',
          key: 'accel_threshold_for_movement',
        },
        {
          command: 'RFMA\r\n',
          key: 'harsh_acceleration_threshold',
        },
        {
          command: 'RFMD\r\n',
          key: 'harsh_braking_threshold',
        },
        {
          command: 'RWTR\r\n',
          key: 'data_transmission_position',
        },
        {
          command: 'RLED\r\n',
          key: 'led_lighting',
        },
        {
          command: 'RLTO\r\n',
          key: 'p2p_mode_duration',
        },
        {
          command: 'RWTO\r\n',
          key: 'lorawan_mode_duration',
        },
        { command: 'RIN1\r\n', key: 'input_1' },
        { command: 'RIN2\r\n', key: 'input_2' },
        { command: 'RIN3\r\n', key: 'input_3' },
        { command: 'RIN4\r\n', key: 'input_4' },
        { command: 'RIN5\r\n', key: 'input_5' },
        { command: 'RIN6\r\n', key: 'input_6' },
        {
          command: 'RC\r\n',
          key: 'full_configuration_table',
        },
        {
          command: 'RFIFO\r\n',
          key: 'fifo_send_and_hold_times',
        },
        {
          command: 'LRWTR\r\n',
          key: 'lorawan_data_transmission_event',
        },
        {
          command: 'LRLTR\r\n',
          key: 'p2p_data_transmission_event',
        },
        {
          command: 'RTS\r\n',
          key: 'data_transmission_status',
        },
        {
          command: 'RF\r\n',
          key: 'full_functionality_table',
        },
        {
          command: 'RACT\r\n',
          key: 'activation_type',
        },
        {
          command: 'RMC\r\n',
          key: 'mcu_configuration',
        },
        {
          command: 'ROUT\r\n',
          key: 'output_table',
        },
      ] as const;

      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const response = await sendMultipleMessages({
              transport: port,
              messages,
            });
            return {
              port,
              equipment,
              config: {
                general: {},
                check: undefined,
                specific: {
                  odometer: BwsNb2LoraParser.odometer(response.odometer),
                  data_transmission_sleep: BwsNb2LoraParser.data_transmission_sleep(
                    response.data_transmission_sleep,
                  ),
                  virtual_ignition_12v: BwsNb2LoraParser.virtual_ignition_12v(
                    response.virtual_ignition_12v,
                  ),
                  virtual_ignition_24v: BwsNb2LoraParser.virtual_ignition_24v(
                    response.virtual_ignition_24v,
                  ),
                  heading: BwsNb2LoraParser.heading(response.heading),
                  heading_event_mode: BwsNb2LoraParser.heading_event_mode(
                    response.heading_event_mode,
                  ),
                  heading_detection_angle: BwsNb2LoraParser.heading_detection_angle(
                    response.heading_detection_angle,
                  ),
                  speed_alert_threshold: BwsNb2LoraParser.speed_alert_threshold(
                    response.speed_alert_threshold,
                  ),
                  accel_threshold_for_ignition_on: BwsNb2LoraParser.accel_threshold_for_ignition_on(
                    response.accel_threshold_for_ignition_on,
                  ),
                  accel_threshold_for_ignition_off:
                    BwsNb2LoraParser.accel_threshold_for_ignition_off(
                      response.accel_threshold_for_ignition_off,
                    ),
                  accel_threshold_for_movement: BwsNb2LoraParser.accel_threshold_for_movement(
                    response.accel_threshold_for_movement,
                  ),
                  harsh_acceleration_threshold: BwsNb2LoraParser.harsh_acceleration_threshold(
                    response.harsh_acceleration_threshold,
                  ),
                  harsh_braking_threshold: BwsNb2LoraParser.harsh_braking_threshold(
                    response.harsh_braking_threshold,
                  ),
                  data_transmission_position: BwsNb2LoraParser.data_transmission_position(
                    response.data_transmission_position,
                  ),
                  led_lighting: BwsNb2LoraParser.led_lighting(response.led_lighting),
                  p2p_mode_duration: BwsNb2LoraParser.p2p_mode_duration(response.p2p_mode_duration),
                  lorawan_mode_duration: BwsNb2LoraParser.lorawan_mode_duration(
                    response.lorawan_mode_duration,
                  ),
                  input_1: BwsNb2LoraParser.input_1(response.input_1),
                  input_2: BwsNb2LoraParser.input_2(response.input_2),
                  input_3: BwsNb2LoraParser.input_3(response.input_3),
                  input_4: BwsNb2LoraParser.input_4(response.input_4),
                  input_5: BwsNb2LoraParser.input_5(response.input_5),
                  input_6: BwsNb2LoraParser.input_6(response.input_6),
                  full_configuration_table: BwsNb2LoraParser.full_configuration_table(
                    response.full_configuration_table,
                  ),
                  fifo_send_and_hold_times: BwsNb2LoraParser.fifo_send_and_hold_times(
                    response.fifo_send_and_hold_times,
                  ),
                  lorawan_data_transmission_event: BwsNb2LoraParser.lorawan_data_transmission_event(
                    response.lorawan_data_transmission_event,
                  ),
                  p2p_data_transmission_event: BwsNb2LoraParser.p2p_data_transmission_event(
                    response.p2p_data_transmission_event,
                  ),
                  data_transmission_status: BwsNb2LoraParser.data_transmission_status(
                    response.data_transmission_status,
                  ),
                  full_functionality_table: BwsNb2LoraParser.full_functionality_table(
                    response.full_functionality_table,
                  ),
                  activation_type: BwsNb2LoraParser.activation_type(response.activation_type),
                  mcu_configuration: BwsNb2LoraParser.mcu_configuration(response.mcu_configuration),
                  output_table: BwsNb2LoraParser.output_table(response.output_table),
                },
              },
              messages: messages.map(({ key, command }) => ({
                key,
                request: command,
                response: response[key],
              })),
            };
          } catch (error) {
            console.error('[ERROR] handleGetConfig', error);
            return { port, equipment, messages: [], config: {} };
          }
        }),
      );
    },
    [sendMultipleMessages],
  );
  const handleConfiguration = useCallback(
    async (detected: Namespace.Detected[], configuration_profile: IConfigurationProfile) => {
      // Configuração NB2 alinhada com o NB2 puro (use-nb-2.ts): envia writes + reads
      // em bloco, faz retry dos reads que voltarem undefined e valida os valores
      // lidos contra o perfil. A parte LoRa (serial LoRa, timestamp, LRKEYS e cadastro
      // na KORA) fica só na identificação — aqui só configuramos os parâmetros NB2.
      const write_messages = typedObjectEntries(generateMessages(configuration_profile)).map(
        ([key, command]) => ({
          key: `w_${key}`,
          command,
          delay_after: [
            'odometer',
            'full_configuration_table',
            'full_functionality_table',
          ].includes(key)
            ? 500
            : 200,
        }),
      );
      const read_messages = [
        {
          command: 'RCN\r\n',
          key: 'r_data_transmission_on',
          delay_before: 1000,
        },
        { command: 'RCW\r\n', key: 'r_data_transmission_off' },
        { command: 'RCE\r\n', key: 'r_data_transmission_event' },
        { command: 'RCK\r\n', key: 'r_keep_alive' },
        { command: 'RIP1\r\n', key: 'r_ip_primary' },
        { command: 'RIP2\r\n', key: 'r_ip_secondary' },
        { command: 'RID1\r\n', key: 'r_dns_primary' },
        { command: 'RID2\r\n', key: 'r_dns_secondary' },
        { command: 'RIAP\r\n', key: 'r_apn' },
        { command: 'RCS\r\n', key: 'r_time_to_sleep' },
        { command: 'RODM\r\n', key: 'r_odometer' },
        { command: 'RIG12\r\n', key: 'r_virtual_ignition_12v' },
        { command: 'RIG24\r\n', key: 'r_virtual_ignition_24v' },
        { command: 'RFA\r\n', key: 'r_heading_detection_angle' },
        { command: 'RFV\r\n', key: 'r_speed_alert_threshold' },
        { command: 'RFTON\r\n', key: 'r_accel_threshold_for_ignition_on' },
        { command: 'RFTOF\r\n', key: 'r_accel_threshold_for_ignition_off' },
        { command: 'RFAV\r\n', key: 'r_accel_threshold_for_movement' },
        { command: 'RFMA\r\n', key: 'r_harsh_acceleration_threshold' },
        { command: 'RFMD\r\n', key: 'r_harsh_braking_threshold' },
        { command: 'RC\r\n', key: 'r_full_configuration_table' },
        { command: 'RF\r\n', key: 'r_full_functionality_table' },
        { command: 'RFSM\r\n', key: 'r_economy_mode' },
        { command: 'ROUT\r\n', key: 'r_lock_type' },
      ] as const;

      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const init_time = Date.now();
            const { pos } = await send({
              transport: port,
              messages: [...write_messages, ...read_messages],
              delayBetweenMessages: 200,
              preChunk: async (transport) => {
                await getReader(transport);
              },
              posChunk: async (transport) => {
                const reader = await getReader(transport);
                const result = await readReader(reader, 30000);
                return result;
              },
            });
            const end_time = Date.now();

            // [DEBUG TEMPORÁRIO] Buffer cru, com bytes de controle escapados (JSON), para
            // diagnosticar a origem do lixo em RIAP/RIP2. Remover após o diagnóstico.
            console.info(
              '[NB2 LoRa][DEBUG] raw buffer lines:',
              (pos ?? []).map((line) => JSON.stringify(line)),
            );

            const write_response = pos
              ? BWSNB2WriteParser.parser(
                  Object.fromEntries(pos.map((line, i) => [`line_${i}`, line])),
                  ...(write_messages.map((msg) => msg.key) as string[]),
                )
              : {};

            let read_response = pos
              ? BWSNB2ReadParser.parser(
                  pos,
                  'r_data_transmission_on',
                  'r_data_transmission_off',
                  'r_data_transmission_event',
                  'r_keep_alive',
                  'r_ip_primary',
                  'r_ip_secondary',
                  'r_dns_primary',
                  'r_dns_secondary',
                  'r_apn',
                  'r_time_to_sleep',
                  'r_odometer',
                  'r_virtual_ignition_12v',
                  'r_virtual_ignition_24v',
                  'r_heading_detection_angle',
                  'r_speed_alert_threshold',
                  'r_accel_threshold_for_ignition_on',
                  'r_accel_threshold_for_ignition_off',
                  'r_accel_threshold_for_movement',
                  'r_harsh_acceleration_threshold',
                  'r_harsh_braking_threshold',
                  'r_full_configuration_table',
                  'r_full_functionality_table',
                  'r_economy_mode',
                  'r_lock_type',
                )
              : {};

            // Retry dos reads que falharam (até 2x), igual ao NB2 puro.
            const maxRetries = 2;
            for (let retry = 0; retry < maxRetries; retry++) {
              const failedKeys = read_messages
                .filter((msg) => read_response[msg.key as keyof typeof read_response] === undefined)
                .map((msg) => msg.key);

              if (failedKeys.length === 0) break;

              console.info(
                `[NB2 LoRa RETRY #${retry + 1}] Retrying ${failedKeys.length} failed reads:`,
                failedKeys,
              );

              const retryMessages = read_messages.filter((msg) => failedKeys.includes(msg.key));

              const { pos: retryPos } = await send({
                transport: port,
                messages: retryMessages,
                delayBetweenMessages: 200,
                preChunk: async (transport) => {
                  await getReader(transport);
                },
                posChunk: async (transport) => {
                  const reader = await getReader(transport);
                  const result = await readReader(reader, 30000);
                  return result;
                },
              });

              if (retryPos) {
                const retry_response = BWSNB2ReadParser.parser(retryPos, ...failedKeys);
                read_response = { ...read_response, ...retry_response };
              }
            }

            // Mapeia as respostas lidas para as chaves do perfil de configuração.
            const expectedToReceivedMap: Record<string, unknown> = {
              keep_alive: read_response.r_keep_alive,
              ip_primary: read_response.r_ip_primary,
              ip_secondary: read_response.r_ip_secondary,
              dns_primary: read_response.r_dns_primary,
              dns_secondary: read_response.r_dns_secondary,
              apn: read_response.r_apn,
              data_transmission_on: read_response.r_data_transmission_on,
              data_transmission_off: read_response.r_data_transmission_off,
              data_transmission_event: read_response.r_data_transmission_event,
              time_to_sleep: read_response.r_time_to_sleep,
              odometer: read_response.r_odometer,
              virtual_ignition_12v: read_response.r_virtual_ignition_12v,
              virtual_ignition_24v: read_response.r_virtual_ignition_24v,
              heading_detection_angle: read_response.r_heading_detection_angle,
              speed_alert_threshold: read_response.r_speed_alert_threshold,
              accel_threshold_for_ignition_on: read_response.r_accel_threshold_for_ignition_on,
              accel_threshold_for_ignition_off: read_response.r_accel_threshold_for_ignition_off,
              accel_threshold_for_movement: read_response.r_accel_threshold_for_movement,
              harsh_acceleration_threshold: read_response.r_harsh_acceleration_threshold,
              harsh_braking_threshold: read_response.r_harsh_braking_threshold,
              full_configuration_table: read_response.r_full_configuration_table,
              full_functionality_table: read_response.r_full_functionality_table,
              economy_mode: read_response.r_economy_mode,
              lock_type: read_response.r_lock_type,
            };

            // 1ª checagem: todos os writes precisam ter recebido resposta.
            const writeCommandsSuccessful = Object.entries(write_response).every(
              ([_, value]) => typeof value !== 'undefined',
            );

            // Ajuste de IP em modo DNS (IP 0.0.0.0): o device retorna a porta do
            // WID1/WID2 em RIP1/RIP2, então alinhamos o valor esperado.
            const adjustExpectedIpForDnsMode = (
              expectedIp: { ip: string; port: number } | undefined,
              expectedDns: { address: string; port: number } | undefined,
            ): { ip: string; port: number } | undefined => {
              if (!expectedIp) return undefined;
              if (expectedIp.ip === '0.0.0.0' && expectedDns) {
                return { ip: expectedIp.ip, port: expectedDns.port };
              }
              return expectedIp;
            };

            const fullExpectedConfig = {
              ...(configuration_profile.config.general ?? {}),
              ...(configuration_profile.config.specific ?? {}),
            } as Record<string, unknown>;

            // Validamos apenas os parâmetros NB2 que possuem comando de leitura
            // correspondente. Campos LoRa do perfil (lorawan_*, p2p_*, wtr) não têm
            // read NB2 e seriam falsos mismatches — a validação LoRa fica para depois.
            const allExpectedConfig = Object.fromEntries(
              Object.entries(fullExpectedConfig).filter(([key]) => key in expectedToReceivedMap),
            ) as Record<string, unknown>;

            if (allExpectedConfig.ip_primary && allExpectedConfig.dns_primary) {
              allExpectedConfig.ip_primary = adjustExpectedIpForDnsMode(
                allExpectedConfig.ip_primary as { ip: string; port: number },
                allExpectedConfig.dns_primary as {
                  address: string;
                  port: number;
                },
              );
            }
            if (allExpectedConfig.ip_secondary && allExpectedConfig.dns_secondary) {
              allExpectedConfig.ip_secondary = adjustExpectedIpForDnsMode(
                allExpectedConfig.ip_secondary as { ip: string; port: number },
                allExpectedConfig.dns_secondary as {
                  address: string;
                  port: number;
                },
              );
            }

            // 2ª checagem: os valores lidos batem com o perfil esperado.
            const validationResult = validateConfiguration(
              allExpectedConfig,
              expectedToReceivedMap,
            );

            if (writeCommandsSuccessful && !validationResult.isValid) {
              console.error('[NB2 LoRa CONFIG VALIDATION FAILED]');
              createValidationLog(validationResult).forEach((log) => {
                console.error(`  ${log}`);
              });
            }

            const status = writeCommandsSuccessful && validationResult.isValid;

            const messages: Array<{
              key: string;
              request: string;
              response?: unknown;
            }> = [...write_messages, ...read_messages].map(({ key, command }) => ({
              key,
              request: command,
              response:
                write_response[key as keyof typeof write_response] ||
                read_response[key as keyof typeof read_response],
            }));

            if (writeCommandsSuccessful && !validationResult.isValid) {
              messages.push({
                key: 'validation_result',
                request: 'CHECK',
                response: '✗ Configuração divergente',
              });
              for (const mismatch of validationResult.mismatches) {
                messages.push({
                  key: `mismatch_${mismatch.parameter}`,
                  request: `CHECK ${mismatch.parameter}`,
                  response: `expected: ${JSON.stringify(mismatch.expected)}, received: ${JSON.stringify(mismatch.received)}`,
                });
              }
            } else if (writeCommandsSuccessful && validationResult.isValid) {
              messages.push({
                key: 'validation_result',
                request: 'CHECK',
                response: '✓ Configuração OK',
              });
            }

            return {
              equipment,
              port,
              init_time,
              end_time,
              status,
              messages,
              applied_profile: {
                specific: {
                  data_transmission_on: read_response.r_data_transmission_on,
                  data_transmission_off: read_response.r_data_transmission_off,
                  data_transmission_event: read_response.r_data_transmission_event,
                  keep_alive: read_response.r_keep_alive,
                  ip_primary: read_response.r_ip_primary,
                  ip_secondary: read_response.r_ip_secondary,
                  dns_primary: read_response.r_dns_primary,
                  dns_secondary: read_response.r_dns_secondary,
                  apn: read_response.r_apn,
                  time_to_sleep: read_response.r_time_to_sleep,
                  odometer: read_response.r_odometer,
                  virtual_ignition_12v: read_response.r_virtual_ignition_12v,
                  virtual_ignition_24v: read_response.r_virtual_ignition_24v,
                  heading_detection_angle: read_response.r_heading_detection_angle,
                  speed_alert_threshold: read_response.r_speed_alert_threshold,
                  accel_threshold_for_ignition_on: read_response.r_accel_threshold_for_ignition_on,
                  accel_threshold_for_ignition_off:
                    read_response.r_accel_threshold_for_ignition_off,
                  accel_threshold_for_movement: read_response.r_accel_threshold_for_movement,
                  harsh_acceleration_threshold: read_response.r_harsh_acceleration_threshold,
                  harsh_braking_threshold: read_response.r_harsh_braking_threshold,
                  full_configuration_table: read_response.r_full_configuration_table,
                  full_functionality_table: read_response.r_full_functionality_table,
                  economy_mode: read_response.r_economy_mode,
                  lock_type: read_response.r_lock_type,
                },
              },
            };
          } catch (error) {
            console.error('[ERROR] handleConfiguration', error);
            return {
              port,
              status: false,
              equipment,
              messages: [],
              init_time: 0,
              end_time: 0,
            };
          }
        }),
      );
    },
    [send, getReader],
  );
  const handleAutoTest = useCallback(
    async (ports: ISerialPort[]) => {
      return await Promise.all(
        ports.map(async (port) => {
          const resultTemplate = {
            port,
            response: {} as Record<string, BwsNb2Lora.AutoTest | string | undefined>,
            messages: [
              { key: 'start', command: 'START\r\n' },
              { key: 'autotest_1', command: 'AUTOTEST' },
              { key: 'autotest_2', command: 'AUTOTEST' },
              { key: 'autotest_3', command: 'AUTOTEST' },
              { key: 'autotest_4', command: 'AUTOTEST' },
              { key: 'autotest_5', command: 'AUTOTEST' },
            ],
            analysis: {} as Record<string, boolean>,
            init_time: Date.now(),
            end_time: 0,
            status: false,
          };

          try {
            // 1. Envia comando START
            const startResponse = await sendMultipleMessages({
              transport: port,
              messages: [{ key: 'start', command: 'START\r\n' }] as const,
            });
            resultTemplate.response.start = startResponse.start;

            // 2. Configuração do loop de AUTOTEST
            const autotestTimeout = 25000; // 25s timeout total
            const startTime = Date.now();
            let remainingAttempts = 5;
            let lastBattResult = false;

            while (remainingAttempts > 0 && Date.now() - startTime < autotestTimeout) {
              const key = `autotest_${5 - remainingAttempts + 1}`;
              try {
                await sleep(2000); // Intervalo entre tentativas

                const autotestResponse = await sendMultipleMessages({
                  transport: port,
                  messages: [
                    {
                      key,
                      command: 'AUTOTEST',
                      // Marcador e timeout explícitos: o device responde "AUTOTEST=...".
                      // O timeout default (500ms) do readResponse é curto e pode fazer a
                      // resposta ser perdida, deixando analysis vazio.
                      check: 'AUTOTEST=',
                      timeout: 4000,
                      transform: BwsNb2LoraParser.auto_test,
                    },
                  ] as const,
                });

                const autotest = autotestResponse[key];

                if (!autotest) continue;

                const BATT_VOLT = Number(autotest.BATT_VOLT);
                const VCC = Number(autotest.VCC);
                const TEMP = Number(autotest.TEMP);

                const isBattGood = !Number.isNaN(BATT_VOLT) && BATT_VOLT <= 43 && BATT_VOLT >= 40;

                resultTemplate.analysis = {
                  DEV: autotest.DEV === 'DM_BWS_NB2_LORA',
                  ACELC: Boolean(autotest.ACELC?.length),
                  ACELP: autotest.ACELP === 'OK',
                  BATT_VOLT: lastBattResult || isBattGood,
                  CHARGER: autotest.CHARGER === 'OK',
                  FW: Boolean(autotest.FW?.length),
                  GPS: autotest.GPS === 'OK',
                  // GPSf: autotest["GPSf"] === "OK",
                  IC: isIccid(autotest.IC ?? ''),
                  ID_ACEL: Boolean(autotest.ID_ACEL?.length),
                  ID_MEM: Boolean(autotest.ID_MEM?.length),
                  IM: isImei(autotest.IM ?? ''),
                  IN1: autotest.IN1 === 'OK',
                  IN2: autotest.IN2 === 'OK',
                  MDM: autotest.MDM === 'OK',
                  OUT: autotest.OUT === 'OK',
                  RSSI: autotest.RSSI === 'OK',
                  SN: Boolean(autotest.SN?.length),
                  VCC: !Number.isNaN(VCC) && VCC <= 130 && VCC >= 120,
                  TEMP: !Number.isNaN(TEMP) && TEMP <= 28 && TEMP >= 23,
                  RF: autotest.RF === 'OK',
                };

                lastBattResult = resultTemplate.analysis.BATT_VOLT;

                const statusValues = Object.values(resultTemplate.analysis);

                resultTemplate.status =
                  statusValues.length > 0 && Object.values(resultTemplate.analysis).every(Boolean);

                resultTemplate.response[key] = autotest;

                if (resultTemplate.status) break;
              } catch (error) {
                console.warn(`Attempt ${5 - remainingAttempts + 1} failed`, error);
              }
              remainingAttempts--;
            }

            resultTemplate.end_time = Date.now();
            return resultTemplate;
          } catch (error) {
            console.error('[ERROR] handleAutoTest', error);
            resultTemplate.end_time = Date.now();
            return resultTemplate;
          }
        }),
      );
    },
    [sendMultipleMessages],
  );
  const handleIdentification = useCallback(
    async (detected: Namespace.Detected, id: string, imeiToWrite?: string) => {
      const { port, equipment } = detected;

      // Timestamp epoch Unix em segundos, em HEX (8 caracteres), gravado junto com o
      // serial. O firmware guarda só 8 caracteres no LWTK: o epoch decimal (10
      // dígitos) voltava truncado no LRTK e a identificação nunca passava. Mesmo
      // formato do WTK do LoRa puro (use-lora.ts).
      const timestamp_to_send = lorawanTimestamp();

      try {
        // Gravação de serial + timestamp, leituras de confirmação e LRKEYS por
        // ÚLTIMO: o dump das chaves (~76 linhas) chega depois das leituras e não
        // contamina as respostas de RINS/LRINS/LRTK. As chaves lidas aqui
        // alimentam o cadastro do dispositivo na KORA. Igual ao NB2 puro, quando o
        // serial já tem IMEI cadastrado em serial-relations, o WIMEI vai primeiro.
        const messages = [
          ...(imeiToWrite ? [{ command: `WIMEI=${imeiToWrite}\r\n`, delay_after: 1000 }] : []),
          { command: `WINS=${id}\r\n`, delay_after: 1000 },
          { command: `LWINS=${id}\r\n`, delay_after: 1000 },
          { command: `LWTK=${timestamp_to_send}\r\n`, delay_after: 1000 },
          // Pausa extra antes das leituras para o dispositivo terminar de
          // persistir o serial/timestamp gravados.
          { command: 'RINS\r\n', delay_before: 2000, delay_after: 500 },
          { command: 'RIMEI\r\n', delay_after: 500 },
          { command: 'LRINS\r\n', delay_after: 500 },
          { command: 'LRTK\r\n', delay_after: 500 },
          { command: 'LRKEYS\r\n' },
        ];

        const hasAllKeys = (source: string[]) =>
          [
            BwsNb2LoraParser.device_address,
            BwsNb2LoraParser.device_eui,
            BwsNb2LoraParser.application_eui,
            BwsNb2LoraParser.application_key,
            BwsNb2LoraParser.application_session_key,
            BwsNb2LoraParser.network_session_key,
          ].every((parser) => source.some((line) => parser(line) !== undefined));

        // A leitura roda EM PARALELO ao envio. Antes o reader só era lido depois de
        // todos os comandos (~10s): as respostas ficavam paradas no buffer da porta
        // (1024 bytes) e, quando ele enchia, o que chegava por último — o dump do
        // LRKEYS — era descartado. Por isso as chaves vinham em uma tentativa e
        // sumiam em outra no mesmo dispositivo.
        const sendAndCollect = async (
          toSend: { command: string; delay_before?: number; delay_after?: number }[],
        ) => {
          let collector: ReturnType<typeof collectLines> | undefined;
          const { pos } = await send({
            transport: port,
            messages: toSend,
            delayBetweenMessages: 200,
            preChunk: async (transport) => {
              collector = collectLines(await getReader(transport));
            },
            posChunk: async () => {
              if (!collector) return [];
              // Encerra quando o dispositivo silencia: 500ms se as chaves já
              // chegaram, 4s caso contrário (o dump pode demorar a começar).
              const start = Date.now();
              while (Date.now() - start < 30000) {
                const idleLimit = hasAllKeys(collector.snapshot()) ? 500 : 4000;
                if (Date.now() - collector.lastDataAt() > idleLimit) break;
                await sleep(100);
              }
              return collector.snapshot();
            },
          });
          return pos ?? [];
        };

        const init_time = Date.now();
        const lines = await sendAndCollect(messages);

        // Retry do LRKEYS (até 2x) quando as chaves não vieram na primeira leitura.
        for (let retry = 0; retry < 2 && !hasAllKeys(lines); retry++) {
          console.info(`[NB2 LoRa RETRY #${retry + 1}] LRKEYS: chaves não lidas, reenviando`);
          lines.push(...(await sendAndCollect([{ command: 'LRKEYS\r\n' }])));
        }
        const end_time = Date.now();

        const pick = <T>(fn: (line: string) => T | undefined): T | undefined =>
          lines.map((line) => fn(line)).find((value) => value !== undefined);

        // NB2 e LoRa respondem o serial com o mesmo prefixo "RINS=" (o LRINS volta
        // sem o "L"), então o parser separa as duas respostas pela ordem de chegada.
        // O firmware devolve o serial sem os zeros à esquerda (ex.: grava 02010A16,
        // responde 2010A16), então completamos até o tamanho do serial gravado
        // antes de comparar.
        const padSerial = (serial: string | undefined) =>
          serial ? serial.toUpperCase().padStart(id.length, '0') : undefined;
        const serials = BwsNb2LoraParser.identification_serials(lines);
        const nb2_serial = padSerial(serials.nb2);
        const lora_serial = padSerial(serials.lora);
        const nb2_imei = pick(BwsNb2LoraParser.imei);
        const timestamp = pick(BwsNb2LoraParser.timestamp)?.toUpperCase();

        // As chaves lidas precisam bater com as geradas pela fórmula do firmware a
        // partir do serial e do timestamp gravados. Todas precisam ter sido lidas
        // para permitir o cadastro na KORA.
        const keys = verifyLorawanKeys(id, timestamp_to_send, {
          device_address: pick(BwsNb2LoraParser.device_address),
          device_eui: pick(BwsNb2LoraParser.device_eui),
          application_eui: pick(BwsNb2LoraParser.application_eui),
          application_key: pick(BwsNb2LoraParser.application_key),
          application_session_key: pick(BwsNb2LoraParser.application_session_key),
          network_session_key: pick(BwsNb2LoraParser.network_session_key),
        });

        const status =
          id === nb2_serial && id === lora_serial && timestamp_to_send === timestamp && keys.valid;

        // Na reprovação, relata cada conferência (esperado x recebido) e as linhas
        // cruas recebidas, para diagnosticar a falha sem precisar reproduzir.
        if (!status) {
          const checks: [string, string, string | undefined][] = [
            ['serial NB2 (RINS)', id, nb2_serial],
            ['serial LoRa (LRINS)', id, lora_serial],
            ['timestamp (LRTK)', timestamp_to_send, timestamp],
            ...typedObjectEntries(keys.expected).map(
              ([key, expected]): [string, string, string | undefined] => [
                key,
                expected,
                keys.read[key],
              ],
            ),
          ];
          console.error(
            [
              '[NB2 LoRa IDENTIFICATION FAILED]',
              ...checks.map(
                ([name, expected, received]) =>
                  `  ${expected === received ? 'ok    ' : 'FALHOU'} ${name}: esperado ${expected}, recebido ${received}`,
              ),
              `  linhas recebidas (${lines.length}):`,
              ...lines.map((line, i) => `  ${i}: ${JSON.stringify(line)}`),
            ].join('\n'),
          );
        }

        return {
          port,
          init_time,
          end_time,
          status,
          messages: [
            ...(imeiToWrite ? [{ key: 'write_imei', request: `WIMEI=${imeiToWrite}\r\n` }] : []),
            { key: 'write_nb2_serial', request: `WINS=${id}\r\n` },
            { key: 'write_lora_serial', request: `LWINS=${id}\r\n` },
            { key: 'write_lora_timestamp', request: `LWTK=${timestamp_to_send}\r\n` },
            { key: 'read_nb2_serial', request: 'RINS\r\n', response: nb2_serial },
            { key: 'read_nb2_imei', request: 'RIMEI\r\n', response: nb2_imei },
            { key: 'read_lora_serial', request: 'LRINS\r\n', response: lora_serial },
            { key: 'read_timestamp', request: 'LRTK\r\n', response: timestamp },
            {
              key: 'read_lora_keys',
              request: 'LRKEYS\r\n',
              // O log de identificação só aceita string em `response`.
              response: keys.complete ? JSON.stringify(keys.read) : undefined,
            },
            ...(keys.complete
              ? [
                  {
                    key: 'check_lora_keys',
                    request: 'CHECK',
                    response: keys.valid
                      ? '✓ Chaves OK'
                      : `✗ Chaves divergentes: ${keys.mismatched.join(', ')}`,
                  },
                ]
              : []),
          ],
          equipment_before: {
            // Dispositivo virgem ainda não tem serial; o log exige string.
            serial: equipment.serial ?? '',
            imei: equipment.imei,
            lora_keys: equipment.lora_keys,
          },
          equipment_after: {
            serial: nb2_serial,
            imei: nb2_imei,
            lora_keys: { timestamp, ...keys.read },
          },
        };
      } catch (error) {
        const message = `[(${new Date().toLocaleString()}) ERROR IN NB2LORA IDENTIFICATION]`;
        console.error(message, error);
        throw new Error(message);
      }
    },
    [send, getReader],
  );
  const handleFirmwareUpdate = useCallback(
    async (detected: Namespace.Detected[]) => {
      return await Promise.all(
        detected.map(async ({ port, equipment }) => {
          try {
            const messages = [
              {
                key: 'update_firmware',
                command: `UPFW\r\n`,
              },
            ] as const;

            const init_time = Date.now();
            const response = await sendMultipleMessages({
              transport: port,
              messages,
            });
            const status = response.update_firmware && response.update_firmware.length > 0;

            const end_time = Date.now();
            return {
              port,
              init_time,
              end_time,
              status,
              messages: messages.map(({ key, command }) => ({
                key,
                request: command,
                response: response[key],
              })),
              equipment: {
                serial: equipment.serial,
                imei: equipment.imei,
                firmware: equipment.firmware,
                lora_keys: equipment.lora_keys,
              },
            };
          } catch (error) {
            const message = `[(${new Date().toLocaleString()}) ERROR IN NB2 + LORA FIRMWARE UPDATE]`;
            console.error(message, error);
            throw new Error(message);
          }
        }),
      );
    },
    [sendMultipleMessages],
  );

  const isIdentified = (input?: {
    imei?: string;
    iccid?: string;
    serial?: string;
    firmware?: string;
  }): 'fully_identified' | 'partially_identified' | 'not_identified' => {
    if (!input) return 'not_identified';
    const { serial, imei, iccid, firmware } = input;
    const identified = [serial, imei, iccid, firmware];
    if (identified.every((e) => e && e.length > 0)) {
      return 'fully_identified';
    } else if (identified.some((e) => e && e.length > 0)) {
      return 'partially_identified';
    } else {
      return 'not_identified';
    }
  };

  return {
    isIdentified,
    ports,
    handleIdentification,
    handleGetConfig,
    handleConfiguration,
    requestPort,
    handleAutoTest,
    handleDetection,
    handleFirmwareUpdate,
  };
};
